import { Injectable, signal } from '@angular/core';
import { Collection, Feature } from 'ol';
import { Geometry, Polygon } from 'ol/geom';
import * as Ol from 'ol'; type OlMap = Ol.Map; 
import { Style, Fill, Circle, Stroke } from 'ol/style';
import TileLayer from 'ol/layer/Tile';
import { ImageWMS, TileWMS } from 'ol/source';
import ImageLayer from 'ol/layer/Image';
import { transformExtent } from 'ol/proj';
import { Sld } from './data/sld';
import { FeatureInfo } from './data/featureinfo';
import { WMSRecord } from './data/wmsrecord';
import { AgChartOptions } from 'ag-charts-community';

//import Static from 'ol/source/ImageStatic';
//import Image from 'ol/layer/Image';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  EPSG_3857 = "EPSG:3857"; // Spherical Mercator projection used by most web map applications (e.g Google, OpenStreetMap, Mapbox).
  EPSG_4326 = "EPSG:4326"; // Standard coordinate system used in cartography, geodesy, and navigation (including GPS).
  map: OlMap|undefined;

  mapstore = {
    aoi: { selectedArea: 'No limit', wkt: undefined },
    type: {active: 'Satellite Street'},
    location: [0, 51.28],
    zoom: 4
  };

  searchLayerFeatures = new Collection<Feature<Geometry>>();
  resultLayerFeatures = new Collection<Feature<Geometry>>();
  basketLayerFeatures = new Collection<Feature<Geometry>>();

  // Key = wmsUrl-wmsLayerName, value = { layer:Layer, record:WMSRecord }
  mapLayersByWmsInfo = new Map();
  // Key = layer, value = record:WMSRecord
  wmsRecordByLayer: Map<any,WMSRecord> = new Map();
  // Key = collection id, value = [ Layer ]
  mapLayersByCollection = new Map();

  featureInfoArray: FeatureInfo[]|undefined = undefined;
  featureInfoChartData: AgChartOptions|undefined = undefined;

  /** ----- MAP STYLES TYPES ----- **/

  /* Red highlight */
  resultStyle = new Style({
//    fill: new Fill({ color: 'rgba(255,128,171,0.2)' }),
    stroke: new Stroke({ color: 'rgba(255,64,129,0.6)', width: 2 }),
    image: new Circle({
        fill: new Fill({
          color: 'rgba(255,128,171,0.2)'
        }),
        radius: 5,
        stroke: new Stroke({
          color: 'rgba(255,64,129,0.6)',
          width: 3
        })
    })
  });

  selectStyle = new Style({
      fill: new Fill({
          color: 'rgba(174,213,129,0.8)'
      }),
      stroke: new Stroke({
          color: 'rgba(85,139,47,0.8)',
          width: 3
      }),
      image: new Circle({
          fill: new Fill({
              color: 'rgba(174,213,129,0.8)'
          }),
          radius: 5,
          stroke: new Stroke({
              color: 'rgba(85,139,47,0.8)',
              width: 3
          })
      })
  });

  hoverStyle = new Style({
//      fill: new Fill({
//          color: 'rgba(41, 182, 246, 0.8)'
//      }),
      stroke: new Stroke({
          color: 'rgba(2, 119, 189, 0.8)',
          width: 3
      }),
      image: new Circle({
          fill: new Fill({
              color: 'rgba(41, 182, 246, 0.8)'
          }),
          radius: 5,
          stroke: new Stroke({
              color: 'rgba(2, 119, 189, 0.8)',
              width: 3
          })
      })
  });

  /* Blue highlight */
  searchBoxStyle = new Style({
      fill: new Fill({ color: 'rgba(188,223,241,0.4)' }),
      stroke: new Stroke({ color: 'rgba(49,112,143,0.4)', width: 2, lineDash: [15, 5] })
  });

  /** ----- END OF MAP STYLES TYPES ----- **/

  constructor() { }

  getPolygonWkt(): any {
    return this.mapstore.aoi.wkt; // TODO: was a copy with angular.copy()
  };
/*
  resetSearchPolygon(): void {
    this.mapstore.aoi.selectedArea = '';
    this.mapstore.aoi.wkt = undefined;
  };
*/
  addResultLayerFeature(feature:Feature): void {
    this.resultLayerFeatures.push(feature);

    // Demo how to add a static png to the map
    // https://docs.maptiler.com/openlayers/examples/raster-layer/
    // if (this.map) {
    //   const l = new Image({
    //     source: new Static({
    //       url: 'https://docs.maptiler.com/openlayers/examples/raster-layer/img/aerial_wgs84.png',
    //       projection: 'EPSG:4326',
    //       imageExtent: [4.639663696289062, 50.89935199434383, 4.642066955566406, 50.900867668253724],
    //     }),
    //   })
    //   this.map.addLayer(l);
    // }
  }

  clearResultLayerFeatures(): void {
    this.resultLayerFeatures.clear();
    this.mapLayersByWmsInfo.forEach((value, key) => {
      this.removeWMSLayer(value.record);
      this.map?.removeLayer(value.layer);
    });
    this.mapLayersByWmsInfo.clear();
    this.wmsRecordByLayer.clear();
    this.mapLayersByCollection.clear();
  }

  highlightResultLayerFeature(feature:Feature|undefined, select:boolean): void {
    if (feature) {
      if (select) {
        feature.setStyle(this.hoverStyle);
      } else {
        feature.setStyle(this.resultStyle);
      }
    }
  }

//  addWMSLayer(wmsUrl:string, layerName:string): any {
  addWMSLayer(rec:WMSRecord) {
    if (this.map && rec.wmsLayerName) {
      var params: {[k: string]: any} = {
          LAYERS: rec.wmsLayerName,
      };

      let sld = undefined;
      if (rec.sld) {
        sld = this.createSLD(rec.wmsLayerName, rec.sld);
      } else if (rec.defaultSld) {
        sld = this.createSLD(rec.wmsLayerName, rec.defaultSld);
      }
      if (sld) {
        params['STYLES'] = 'SLD';
        params['SLD_BODY'] = sld;
      };
      let opacity:number = 1.0;
      if (rec.wmsopacity) {
        opacity = rec.wmsopacity;
      }
      rec.wmsopacity = opacity;
      let layer = new ImageLayer({
        opacity: opacity,
        source: new ImageWMS({
          url: rec.wmsUrl,
          params: params
        }),
        zIndex: 0
      });

      rec.wmsMapLayer = layer;
      this.map.addLayer(layer);
      this.mapLayersByWmsInfo.set(rec.wmsUrl+'-'+rec.wmsLayerName, {layer:layer, record:rec});
      this.wmsRecordByLayer.set(layer, rec);
      if (rec.collection) {
        if (rec.collection in this.mapLayersByCollection) {
          this.mapLayersByCollection.get(rec.collection).push(layer);
        } else {
          this.mapLayersByCollection.set(rec.collection, [ layer ]);
        }
      }
      // Zoom to the new layer
      this.zoomToWGS84(rec.bbox);
/*
        new TileLayer({
        source: new TileWMS({
          projection: 'EPSG:4326',
          url: wmsUrl,
          params: {
            'LAYERS': layerName,
          },
        }),
        })
*/
    }
  }

  zoomToWGS84(extent:any) {
    if (this.map && extent) {
      const extent3857 = transformExtent(
        extent,
        'EPSG:4326',
        'EPSG:3857'
      );
      this.map.getView().fit(extent3857);
    }
  }

  removeWMSLayer(rec:WMSRecord) {
    let key = rec.wmsUrl+'-'+rec.wmsLayerName;
    if (this.mapLayersByWmsInfo.has(key)) {
      let value = this.mapLayersByWmsInfo.get(key);

      this.map?.removeLayer(value.layer);

      this.mapLayersByWmsInfo.delete(key);
      this.wmsRecordByLayer.delete(value.layer);

      if (rec.collection) {
        if (this.mapLayersByCollection.has(rec.collection)) {
          let layers = this.mapLayersByCollection.get(rec.collection);
          const index = layers.indexOf(value.layer, 0);
          if (index > -1) {
            layers.splice(index, 1);
          }
          if (layers.length == 0) {
            this.mapLayersByCollection.delete(rec.collection);
          }
        }
      }
    }
    rec.wmsMapLayer = undefined;
  }

  getWMSLayer(rec:WMSRecord) {
    let key = rec.wmsUrl+'-'+rec.wmsLayerName;
    return this.mapLayersByWmsInfo.get(key);
  }

  getWMSRecordForLayer(layer:any) {
    return this.wmsRecordByLayer.get(layer);
  }

  hasCollectionWMSLayer(collection:string) {
    return this.mapLayersByCollection.has(collection);
  }

  removeCollectionWMSLayers(collection:string) {
    for (let key of this.mapLayersByWmsInfo.keys()) {
      let value = this.mapLayersByWmsInfo.get(key);
      if (value.record.collection == collection) {
        this.removeWMSLayer(value.record);
      }
    }
  }

  setCollectionWMSLayersOpacity(collection:string, opacity:number) {
    for (let key of this.mapLayersByWmsInfo.keys()) {
      let value = this.mapLayersByWmsInfo.get(key);
      if (value.record.collection == collection) {
        this.setWMSLayerOpacity(value.record, opacity);
      }
    }
  }

  setWMSLayerOpacity(rec:WMSRecord, opacity:number) {
    let key = rec.wmsUrl+'-'+rec.wmsLayerName;
    if (this.mapLayersByWmsInfo.has(key)) {
      let value = this.mapLayersByWmsInfo.get(key);
      value.layer.setOpacity(opacity);
      rec.wmsopacity = opacity;
    }
  }

  getSearchBoundingBox(): number[]|undefined {
    if (this.searchLayerFeatures.getLength() == 1) {
      let feature = this.searchLayerFeatures.getArray()[0];
      let geom = feature.getGeometry();
      if (geom) {
        let extent = geom.getExtent();
        if (extent) {
          return transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
        }
      }
    }
    return undefined;
  }

  createSLD(layerName:string, sld:Sld) {
    let sldString = '<?xml version=\"1.0\" encoding=\"ISO-8859-1\"?><StyledLayerDescriptor version=\"1.0.0\" xsi:schemaLocation=\"http://www.opengis.net/sld StyledLayerDescriptor.xsd\" xmlns=\"http://www.opengis.net/sld\" xmlns:ogc=\"http://www.opengis.net/ogc\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:xsi=\"http://www.w3.org/2001/XMLSchema-instance\"><NamedLayer><Name>';
    sldString += layerName;
    sldString += '</Name><UserStyle><Title>SLD</Title><IsDefault>1</IsDefault><FeatureTypeStyle><Rule><RasterSymbolizer>';
    // ChannelSelection
    if (sld.hasChannelSelection()) {
      sldString += '<ChannelSelection>';
      if (sld.red && sld.green && sld.blue) {
        sldString += '<RedChannel>';
        sldString += '<SourceChannelName>' + sld.red.bandnumber + '</SourceChannelName>';
        if (sld.red.minvalue !== undefined && sld.red.maxvalue !== undefined) {
                  sldString += '<ContrastEnhancement>';
                  sldString += '<Normalize>';
                  sldString += '<VendorOption name="algorithm">StretchToMinimumMaximum</VendorOption>';
                  sldString += '<VendorOption name="minValue">' + sld.red.minvalue + '</VendorOption>';
                  sldString += '<VendorOption name="maxValue">' + sld.red.maxvalue + '</VendorOption>';
                  sldString += '</Normalize>';
                  sldString += '</ContrastEnhancement>';
        }
        sldString += '</RedChannel>';
        sldString += '<GreenChannel>';
        sldString += '<SourceChannelName>' + sld.green.bandnumber + '</SourceChannelName>';
        if (sld.red.minvalue !== undefined && sld.green.maxvalue !== undefined) {
                  sldString += '<ContrastEnhancement>';
                  sldString += '<Normalize>';
                  sldString += '<VendorOption name="algorithm">StretchToMinimumMaximum</VendorOption>';
                  sldString += '<VendorOption name="minValue">' + sld.green.minvalue + '</VendorOption>';
                  sldString += '<VendorOption name="maxValue">' + sld.green.maxvalue + '</VendorOption>';
                  sldString += '</Normalize>';
                  sldString += '</ContrastEnhancement>';
        }
        sldString += '</GreenChannel>';
        sldString += '<BlueChannel>';
        sldString += '<SourceChannelName>' + sld.blue.bandnumber + '</SourceChannelName>';
        if (sld.red.minvalue !== undefined && sld.blue.maxvalue !== undefined) {
                  sldString += '<ContrastEnhancement>';
                  sldString += '<Normalize>';
                  sldString += '<VendorOption name="algorithm">StretchToMinimumMaximum</VendorOption>';
                  sldString += '<VendorOption name="minValue">' + sld.blue.minvalue + '</VendorOption>';
                  sldString += '<VendorOption name="maxValue">' + sld.blue.maxvalue + '</VendorOption>';
                  sldString += '</Normalize>';
                  sldString += '</ContrastEnhancement>';
        }
        sldString += '</BlueChannel>';
      } else if (sld.gray) {
        sldString += '<GrayChannel>';
        sldString += '<SourceChannelName>' + sld.gray.bandnumber + '</SourceChannelName>';
        if (sld.gray.minvalue !== undefined && sld.gray.maxvalue !== undefined) {
                  sldString += '<ContrastEnhancement>';
                  sldString += '<Normalize>';
                  sldString += '<VendorOption name="algorithm">StretchToMinimumMaximum</VendorOption>';
                  sldString += '<VendorOption name="minValue">' + sld.gray.minvalue + '</VendorOption>';
                  sldString += '<VendorOption name="maxValue">' + sld.gray.maxvalue + '</VendorOption>';
                  sldString += '</Normalize>';
                  sldString += '</ContrastEnhancement>';
        }
        sldString += '</GrayChannel>';
      }
      sldString += '</ChannelSelection>';
    }

    // ColorMap
    if (sld.colormap) {
      sldString += '<ColorMap>';
          for (var i=0; i<sld.colormap.length; i++) {
            sldString += '<ColorMapEntry color=\"' + sld.colormap[i].color 
              + '\" quantity=\"' + sld.colormap[i].quantity 
              + '\" opacity=\"' + sld.colormap[i].alpha + '\"/>';
          }
          sldString += '</ColorMap>';
    }
    sldString += '</RasterSymbolizer></Rule></FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>';
    return sldString;
  }
}
